"use client";

import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Echtzeit-3D-Wissensgraph im Altbestand (Brownfield):
 * Ein dichtes 3D-Knotennetz rotiert im Raum, während ein goldener/smaragdgrüner
 * Pfadfinder-Impuls die kritische Route durch den Legacy-Dschungel aufleuchten lässt.
 */
export default function Preview() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let angle = 0;

    // 12 Knoten im 3D-Raum (Indices 0 -> 1 -> 2 -> 3 bilden den goldenen Pfad!)
    const nodes = [
      { pos: [-0.85, 0.35, 0.1], pathIdx: 0 },
      { pos: [-0.25, 0.15, 0.45], pathIdx: 1 },
      { pos: [0.3, -0.15, 0.25], pathIdx: 2 },
      { pos: [0.85, -0.35, -0.1], pathIdx: 3 },
      // Graue Legacy-Beifang-Knoten
      { pos: [-0.6, -0.5, -0.4], pathIdx: -1 },
      { pos: [-0.1, 0.65, -0.35], pathIdx: -1 },
      { pos: [0.55, 0.5, -0.45], pathIdx: -1 },
      { pos: [0.15, -0.65, 0.5], pathIdx: -1 },
      { pos: [-0.45, -0.1, -0.65], pathIdx: -1 },
      { pos: [0.65, 0.2, 0.55], pathIdx: -1 },
    ];

    const backgroundEdges = [
      [0, 4],
      [4, 8],
      [1, 5],
      [5, 6],
      [2, 7],
      [6, 3],
      [8, 2],
      [9, 3],
      [1, 4],
      [5, 9],
    ];

    const render = (time: number) => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
      }
      ctx.save();
      ctx.scale(dpr, dpr);
      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);

      angle = Math.sin(time * 0.001) * 0.45;
      const cosY = Math.cos(angle);
      const sinY = Math.sin(angle);

      const projected = nodes.map((n) => {
        const [x, y, z] = n.pos;
        const rx = x * cosY - z * sinY;
        const rz = z * cosY + x * sinY;
        const fov = 2.8;
        const scale = (Math.min(w, h) * 0.48 * fov) / (fov + rz);
        return {
          px: w / 2 + rx * scale,
          py: h / 2 + y * scale,
          pathIdx: n.pathIdx,
        };
      });

      // 1. Zeichne verworrene Legacy-Kanten im Hintergrund
      ctx.strokeStyle = "rgba(148, 163, 184, 0.25)";
      ctx.lineWidth = 1.2;
      for (const [a, b] of backgroundEdges) {
        ctx.beginPath();
        ctx.moveTo(projected[a].px, projected[a].py);
        ctx.lineTo(projected[b].px, projected[b].py);
        ctx.stroke();
      }

      // 2. Zeichne den leuchtenden Agenten-Pfad (0 -> 1 -> 2 -> 3)
      ctx.strokeStyle = "#10b981";
      ctx.shadowColor = "#10b981";
      ctx.shadowBlur = 12;
      ctx.lineWidth = 2.8;
      ctx.beginPath();
      ctx.moveTo(projected[0].px, projected[0].py);
      ctx.lineTo(projected[1].px, projected[1].py);
      ctx.lineTo(projected[2].px, projected[2].py);
      ctx.lineTo(projected[3].px, projected[3].py);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // 3. Zeichne alle Knoten
      for (const p of projected) {
        ctx.beginPath();
        const isPath = p.pathIdx >= 0;
        ctx.arc(p.px, p.py, isPath ? 6 : 3.5, 0, Math.PI * 2);
        if (isPath) {
          ctx.fillStyle = p.pathIdx === 3 ? "#10b981" : "#a855f7";
          ctx.shadowColor = p.pathIdx === 3 ? "#10b981" : "#a855f7";
          ctx.shadowBlur = 12;
        } else {
          ctx.fillStyle = "rgba(148, 163, 184, 0.45)";
          ctx.shadowBlur = 0;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      ctx.restore();
      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl select-none"
    >
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.75, 0.4] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute h-36 w-48 rounded-full bg-purple-500/25 blur-3xl"
      />
      <canvas ref={canvasRef} className="h-full w-full" />
      <div className="pointer-events-none absolute bottom-2.5 rounded-full border border-emerald-500/40 bg-white/90 px-3 py-0.5 font-mono text-[10px] font-extrabold text-emerald-700 shadow-md backdrop-blur-md dark:bg-zinc-900/90 dark:text-emerald-300">
        Code-Graph statt Raten ✓
      </div>
    </div>
  );
}
